/* KeyboardState — whose keystroke was it: the person's, or the keyboard's?
   ────────────────────────────────────────────────────────────────────────────
   A typing tool compares the typed character with the expected one. Two
   states of the keyboard make every key "wrong" although the person pressed
   the right one:
     · the layout is switched to another alphabet (a Russian text typed with
       the English layout on: «быть» arrives as ",snm");
     · Caps Lock is on (capitals where the text has small letters).
   Scoring those as typos punishes the keyboard, not the typing: the run goes
   red, the rank is «Улитка», and the tool never says what is wrong.

   Measured on /typing-speed-test, 09–29.10.2026 (ru): 264 of 2409 sessions
   opened with a run under 25 % accuracy; those runs had 5–6 errors per
   correct key — exactly the English layout on this text (letters wrong,
   spaces right; 5.25 in the lab) — and 121 of them left without another try.
   /typing-trainer: 1395 of 17788 runs under 20 % accuracy, same signature.

     KeyboardState.miss(typed, expected, capsOn) → 'layout' | 'caps' | ''
         'layout' — a letter of another writing system than the expected
                    letter (or ASCII punctuation where a Cyrillic or Arabic
                    letter is expected: those layouts keep letters on the
                    keys that type [ ] ; ' , . ` in a Latin one);
         'caps'   — Caps Lock is on and a capital came where the text has a
                    small letter. Any capital, not only the capital of the
                    expected letter: the tool waits on that letter, and the
                    person keeps typing the letters after it.
         ''       — the keystroke is the person's: score it.
     KeyboardState.fixes(typed, expected) → true when the keystroke is a
         letter of the expected letter's writing system, i.e. the keyboard
         is fixed and scoring may go on.
     KeyboardState.matches(typed, expected) → the comparison itself, with
         Russian spelling's «е» for «ё».
     KeyboardState.partial(text) → true while an IME composition is still
         building one character (a Hangul syllable, a dead-key accent): score
         it when the composition ends.
     KeyboardState.scriptOf(ch) → 'latin' | 'cyrillic' | 'arabic' | 'hangul' | ''

   Contract test — tests/js/keyboard-state.test.mjs. Why — docs/why/typing-keyboard-state.md. */
(function (root) {
    'use strict';

    function scriptOf(ch) {
        if (!ch) return '';
        var c = ch.charCodeAt(0);
        if ((c >= 65 && c <= 90) || (c >= 97 && c <= 122) || (c >= 0xC0 && c <= 0x24F && c !== 0xD7 && c !== 0xF7)) return 'latin';
        if (c >= 0x400 && c <= 0x52F) return 'cyrillic';
        if ((c >= 0x600 && c <= 0x6FF) || (c >= 0x750 && c <= 0x77F) || (c >= 0xFB50 && c <= 0xFDFF) || (c >= 0xFE70 && c <= 0xFEFF)) return 'arabic';
        if ((c >= 0xAC00 && c <= 0xD7A3) || (c >= 0x1100 && c <= 0x11FF) || (c >= 0x3130 && c <= 0x318F)) return 'hangul';
        return '';
    }

    var LATIN_KEY_PUNCT = /^[!-\/:-@\[-`{-~]$/;

    function miss(typed, expected, capsOn) {
        if (!typed || !expected) return '';
        var want = scriptOf(expected);
        if (!want) return '';
        var got = scriptOf(typed);
        if (got && got !== want) return 'layout';
        if (!got && (want === 'cyrillic' || want === 'arabic') && LATIN_KEY_PUNCT.test(typed)) return 'layout';
        if (capsOn && expected !== expected.toUpperCase() && typed !== typed.toLowerCase()) return 'caps';
        return '';
    }

    function fixes(typed, expected) {
        var s = scriptOf(typed);
        return !!s && s === scriptOf(expected);
    }

    function matches(typed, expected) {
        return typed === expected || (expected === 'ё' && typed === 'е') || (expected === 'Ё' && typed === 'Е');
    }

    function partial(text) {
        if (!text) return false;
        var last = text.charAt(text.length - 1);
        var c = last.charCodeAt(0);
        return (c >= 0xAC00 && c <= 0xD7A3) || (c >= 0x1100 && c <= 0x11FF) || (c >= 0x3130 && c <= 0x318F)
            || (c >= 0x300 && c <= 0x36F) || '´`^¨~˜¸ˆ'.indexOf(last) >= 0;
    }

    root.KeyboardState = { scriptOf: scriptOf, miss: miss, fixes: fixes, matches: matches, partial: partial };
})(typeof window !== 'undefined' ? window : this);
